// The catalogue → drawing sync: "Update drawing" in a product's right-click menu
// brings every placed copy of a product up to its catalogue entry.
// docs/claude/uiModules.md.
import { productName } from '../../shared/names';
import {
  type Connection,
  type DrawingGroup,
  type PipeType,
  type ProductInstance,
  type ProductTemplate,
} from '../../shared/types';
import {
  placeCopy,
  refreshCopy,
} from '../catalogueCopy';
import {
  invalidate,
  newId,
  pushHistory,
  renderProperties,
  state,
} from './core';
import { showConfirm } from '../dialog';
import {
  groupById,
  groupChain,
  groupMembers,
  matchParts,
} from '../groups';
import {
  getUiLang,
  tr,
} from '../i18n';
import { relaidPartConnections } from '../multipart';
import { refreshVariant } from '../variants';
import {
  regenerateSauna,
  syncDimensions,
} from '../main';
import { removeObjectsById } from './deleteSelection';
import { pipeTypeChoices } from './connectionUi';

/** A fresh id for a copy's rough-in. */
export const prepId = (): string => newId('prep');

/** Re-run the layout-preserving fix-ups a spec update may require. */
export function postUpdateFixups(inst: ProductInstance): void {
  if (inst.autoBenches) regenerateSauna(inst); // re-bake bench fills around the kept layout
}

/** Number of placed things this catalog product has in the drawing: instance
 *  count for single-body, distinct group count for multi-part. */
export function drawingInstanceCount(t: ProductTemplate): number {
  if (t.parts && t.parts.length) return productGroupsOf(t.id).length;
  return state.drawing.instances.filter((i) => i.templateId === t.id).length;
}

/** The placed copies of a multi-part product: the groups linked to it. */
function productGroupsOf(templateId: string): DrawingGroup[] {
  return (state.drawing.groups || []).filter((g) => g.source?.templateId === templateId);
}

/** The parts of a placed multi-part product (its linked group). */
export function productParts(gid: string): ProductInstance[] {
  const ids = new Set(groupMembers(state.drawing, gid).map((m) => m.id));
  return state.drawing.instances.filter((i) => ids.has(i.id));
}

/** The linked product group a part belongs to, if any. */
export function productGroupOf(inst: ProductInstance): DrawingGroup | undefined {
  for (const gid of groupChain(state.drawing, inst.groupId)) {
    const g = groupById(state.drawing, gid);
    if (g?.source) return g;
  }
  return undefined;
}

/** Update every placed instance of `t` in the drawing with the catalog item's
 *  current spec/material fields (preserving each object's position, size and
 *  drawn layout). Works for multi-part products (parts matched by name, with
 *  added/removed parts and connections reconciled). */
export async function updateDrawingFromTemplate(t: ProductTemplate): Promise<void> {
  const count = drawingInstanceCount(t);
  if (count === 0) return;
  const label = productName(t, getUiLang());
  if (!(await showConfirm(`“${label}” × ${count}\n\n${tr('Colours, geometry, heater settings, preparations and properties are refreshed. Each object keeps its position, size and drawn layout.')}`, { title: 'Update the placed copies from the catalog?', ok: 'Update' }))) return;
  pushHistory();
  if (t.parts && t.parts.length) updateMultiPartGroups(t);
  else for (const inst of state.drawing.instances.filter((i) => i.templateId === t.id)) { refreshCopy(inst, t, 'catalogue', prepId); postUpdateFixups(inst); }
  syncDimensions();
  renderProperties();
  invalidate();
}

/** Reconcile every placed group of a multi-part product with the catalog: update
 *  matching part-instances in place, add parts introduced in the catalog, delete
 *  parts removed from it, and rebuild the group's connections. */
function updateMultiPartGroups(t: ProductTemplate): void {
  for (const g of productGroupsOf(t.id)) {
    const parts = productParts(g.id);
    if (parts.length) reconcileGroupFromTemplate(t, g.id, parts, true);
  }
}

/** Reconcile ONE placed group with a template: the body of the catalog-wide
 *  update above, split out so a single placed group can be re-laid from a
 *  template nothing else shares (the per-part edit). */
export function reconcileGroupFromTemplate(t: ProductTemplate, groupId: string, insts: ProductInstance[], fromCatalog: boolean): void {
  const parts = t.parts || [];
  // Parts match by `partId` (so a part renamed in the drawing is kept, with its
  // position and connections), falling back to the name for older drawings.
  const { matched, extra } = matchParts(insts, parts);
  // Recover the group origin from the first part's instance, so any parts newly
  // added in the catalog land at anchor + their offset.
  const [k0, i0] = matched.has(0) ? [0, matched.get(0)!] : [...matched.entries()][0] ?? [-1, insts[0]];
  const anchor = k0 >= 0 && i0 ? { x: i0.x - parts[k0].offset.x, y: i0.y - parts[k0].offset.y } : { x: i0?.x ?? 0, y: i0?.y ?? 0 };
  const source = groupById(state.drawing, groupId)?.source;
  if (source) { const v = refreshVariant(t.variants, source.variant); if (v) source.variant = v; }
  const idOfPart = new Map<number, string>();
  for (const [k, inst] of matched) {
    refreshCopy(inst, t, fromCatalog ? 'catalogue' : 'edit', prepId, { part: parts[k], first: k === 0 });
    inst.partId = parts[k].id;
    postUpdateFixups(inst);
    idOfPart.set(k, inst.id);
  }
  // Removed from the catalog (or a duplicate) → drop it and its connections.
  if (extra.length) removeObjectsById(new Set(extra.map((inst) => inst.id)));
  // Add parts introduced in the catalog since this group was placed.
  parts.forEach((part, k) => {
    if (idOfPart.has(k)) return;
    const ni: ProductInstance = {
      ...placeCopy(t, prepId, { part, first: k === 0 }),
      id: newId('mp'), groupId, partId: part.id,
      x: anchor.x + part.offset.x, y: anchor.y + part.offset.y, rotation: part.rotation || 0,
    };
    state.drawing.instances.push(ni);
    idOfPart.set(k, ni.id);
  });
  // Rebuild the group's connections from the template (part → instance), each
  // in the place of the one between the same two parts (`relaidPartConnections`).
  const ids = new Set(idOfPart.values());
  const oldConns = state.drawing.connections.filter((c) => ids.has(c.from) && ids.has(c.to));
  state.drawing.connections = state.drawing.connections.filter((c) => !oldConns.includes(c));
  const idOf = new Map([...idOfPart].map(([k, id]) => [parts[k].id, id]));
  state.drawing.connections.push(...relaidPartConnections(t, idOf, oldConns, () => newId('mp')));
  adoptPipeTypes(state.drawing.connections);
}

/** Copy into the drawing every pipe type the connections `conns` are made of
 *  that it has no copy of yet (a product's hoses, placed with it). */
export function adoptPipeTypes(conns: readonly Connection[]): void {
  for (const c of conns) {
    if (!c.pipe || state.drawing.pipeTypes?.some((t) => t.id === c.pipe!.type)) continue;
    const t = pipeTypeChoices().find((x) => x.id === c.pipe!.type);
    if (!t) continue;
    const copy: PipeType = structuredClone(t);
    delete copy.rowId;
    (state.drawing.pipeTypes ??= []).push(copy);
  }
}
